import { useState, useRef } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { CryptoAsset, formatCurrency, formatCrypto } from "@/lib/crypto-data";
import { Clock, AlertTriangle, Upload, Loader2, ShieldCheck, XCircle, CreditCard, ExternalLink, CheckCircle2, KeyRound } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { z } from "zod";

interface PurchaseModalProps {
  crypto: CryptoAsset | null;
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

type PaymentMethod = "yqqy_card" | "manual_proof";
type Step = "details" | "payment_method" | "card_code" | "proof" | "verifying" | "result";

const PurchaseModal = ({ crypto, isOpen, onClose, onSuccess }: PurchaseModalProps) => {
  const { user } = useAuth();
  const { toast } = useToast();
  const [step, setStep] = useState<Step>("details");
  const [paymentMethod, setPaymentMethod] = useState<PaymentMethod | null>(null);
  const [loading, setLoading] = useState(false);
  const [cryptoInput, setCryptoInput] = useState("");
  const [errors, setErrors] = useState<Record<string, string>>({});
  
  // Card code state
  const [cardCode, setCardCode] = useState("");
  const [cardBalance, setCardBalance] = useState<number | null>(null);
  const [checkingCard, setCheckingCard] = useState(false);
  const [cardError, setCardError] = useState("");
  
  // Recipient wallet (user's on-chain ETH address) and processing state
  const [walletAddress, setWalletAddress] = useState("");
  const [walletError, setWalletError] = useState("");
  const [processingCountdown, setProcessingCountdown] = useState(60);
  const [processingTxId, setProcessingTxId] = useState<string | null>(null);
  
  // Manual proof state
  const [proofFile, setProofFile] = useState<File | null>(null);
  const [proofPreview, setProofPreview] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  
  // Result state
  const [verificationResult, setVerificationResult] = useState<{ approved: boolean; analysis: string; transactionNumber?: string } | null>(null);

  if (!crypto) return null;

  const cryptoAmount = cryptoInput ? parseFloat(cryptoInput) : 0;
  const amount = cryptoAmount > 0 ? (cryptoAmount * crypto.sellPrice).toFixed(2) : "";

  const handleDetailsNext = () => {
    try {
      z.object({
        amount: z.number().min(5, "الحد الأدنى للشراء 5 دولار").max(100000000, "الحد الأقصى 100,000,000 دولار"),
      }).parse({ amount: parseFloat(amount) });
      setErrors({});
      setStep("payment_method");
    } catch (error) {
      if (error instanceof z.ZodError) {
        const e: Record<string, string> = {};
        error.errors.forEach((err) => { e[err.path[0]] = err.message; });
        setErrors(e);
      }
    }
  };

  const handleCheckCardBalance = async () => {
    if (!cardCode.trim()) {
      setCardError("أدخل رمز البطاقة");
      return;
    }
    setCheckingCard(true);
    setCardError("");
    setCardBalance(null);

    try {
      const { data, error } = await supabase.functions.invoke("redeem-card", {
        body: { action: "check_balance", code: cardCode.trim() },
      });

      if (error) throw error;
      if (data.error) {
        setCardError(data.error === "Card not found" ? "البطاقة غير موجودة" : data.error);
        return;
      }

      const card = data.card;
      if (card.status !== "active") {
        setCardError("هذه البطاقة غير نشطة أو مستخدمة بالكامل");
        return;
      }

      setCardBalance(card.current_balance);

      if (card.current_balance < parseFloat(amount)) {
        setCardError(`رصيد البطاقة ($${card.current_balance}) أقل من المبلغ المطلوب ($${amount})`);
      }
    } catch (e) {
      console.error("Check balance error:", e);
      setCardError("فشل التحقق من البطاقة. حاول مرة أخرى.");
    } finally {
      setCheckingCard(false);
    }
  };

  const handlePayWithCard = async () => {
    if (!user || !cardCode.trim()) return;

    // Auto-resolve user's Polygon receive address
    let addr = "";
    try {
      const { data: row } = await (supabase as any)
        .from("user_crypto_addresses")
        .select("address")
        .eq("user_id", user.id)
        .eq("network", "Polygon (POS)")
        .maybeSingle();
      addr = row?.address || "";
    } catch (e) {
      console.error("resolve address error:", e);
    }
    if (!/^0x[a-fA-F0-9]{40}$/.test(addr)) {
      toast({
        title: "لا يوجد عنوان Polygon",
        description: "لم يتم العثور على عنوان استلام Polygon لحسابك. حاول لاحقاً.",
        variant: "destructive",
      });
      return;
    }

    const totalAmount = parseFloat(amount);
    setStep("verifying");
    setLoading(true);

    try {
      const { data, error } = await supabase.functions.invoke("redeem-card", {
        body: {
          action: "use_balance",
          code: cardCode.trim(),
          amount: totalAmount,
          cryptoSymbol: crypto.symbol,
          cryptoName: crypto.name,
          cryptoAmount,
          priceUsd: crypto.sellPrice,
        },
      });

      if (error) throw error;
      if (data.error) {
        toast({ title: "فشل", description: data.error, variant: "destructive" });
        setStep("card_code");
        return;
      }

      // Card debited — now mark transaction as pending while on-chain swap runs
      const txId = data.transaction_id as string;
      await supabase.from("transactions").update({
        status: "pending",
        wallet_address: addr,
        user_wallet_address: addr,
        network: "Polygon (POS)",
      }).eq("id", txId);

      setProcessingTxId(txId);
      setProcessingCountdown(60);

      // Fire-and-forget: trigger on-chain swap + transfer from admin wallet
      supabase.functions.invoke("process-purchase-swap", {
        body: {
          transactionId: txId,
          userWalletAddress: addr,
          cryptoSymbol: crypto.symbol,
          totalUsd: totalAmount,
        },
      }).catch((err) => console.error("process-purchase-swap invoke error:", err));

      // Start countdown + polling
      const startedAt = Date.now();
      const countdownInterval = setInterval(() => {
        const elapsed = Math.floor((Date.now() - startedAt) / 1000);
        setProcessingCountdown(Math.max(0, 60 - elapsed));
      }, 1000);

      const pollUntil = Date.now() + 4 * 60 * 1000; // up to 4 minutes
      const pollInterval = setInterval(async () => {
        const { data: row } = await supabase
          .from("transactions")
          .select("status, transfer_tx_hash, swap_tx_hash")
          .eq("id", txId)
          .maybeSingle();

        if (row?.transfer_tx_hash || row?.status === "approved") {
          clearInterval(pollInterval);
          clearInterval(countdownInterval);
          setVerificationResult({
            approved: true,
            analysis: `تم إرسال ${crypto.symbol} مباشرة إلى عنوانك على شبكة Polygon. هاش العملية: ${row?.transfer_tx_hash || row?.swap_tx_hash || ""}`,
            transactionNumber: data.transaction_number,
          });
          setStep("result");
        } else if (Date.now() > pollUntil) {
          clearInterval(pollInterval);
          clearInterval(countdownInterval);
          setVerificationResult({
            approved: false,
            analysis: "العملية قيد المعالجة على البلوكتشين. سيتم إكمالها تلقائياً وستظهر في سجل المعاملات.",
            transactionNumber: data.transaction_number,
          });
          setStep("result");
        }
      }, 5000);
    } catch (e) {
      console.error("Card payment error:", e);
      toast({ title: "خطأ", description: "فشل في إتمام العملية", variant: "destructive" });
      setStep("card_code");
    } finally {
      setLoading(false);
    }
  };

  // Manual proof handlers (existing logic)
  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      toast({ title: "خطأ", description: "حجم الملف يتجاوز 5MB", variant: "destructive" });
      return;
    }
    setProofFile(file);
    const reader = new FileReader();
    reader.onload = (ev) => setProofPreview(ev.target?.result as string);
    reader.readAsDataURL(file);
  };

  const handleSubmitWithProof = async () => {
    if (!user || !proofFile) return;
    setStep("verifying");
    setLoading(true);

    try {
      const totalAmount = parseFloat(amount);
      const { data: txData, error: txError } = await supabase.from("transactions").insert({
        user_id: user.id,
        crypto_symbol: crypto.symbol,
        crypto_name: crypto.name,
        amount: cryptoAmount,
        price_usd: crypto.sellPrice,
        total_usd: totalAmount,
        card_last_four: "0000",
        card_type: "internal",
        wallet_address: "internal_wallet",
        network: "internal",
        network_fee: 0,
        status: "pending",
      }).select("id").single();

      if (txError) throw txError;

      const ext = proofFile.name.split(".").pop() || "jpg";
      const filePath = `${user.id}/${txData.id}.${ext}`;
      const { error: uploadError } = await supabase.storage
        .from("payment-proofs")
        .upload(filePath, proofFile, { upsert: true });

      if (uploadError) throw uploadError;

      const { data: urlData } = supabase.storage.from("payment-proofs").getPublicUrl(filePath);

      const { data: verifyData, error: verifyError } = await supabase.functions.invoke("verify-payment", {
        body: {
          transactionId: txData.id,
          proofUrl: urlData.publicUrl,
          cardLast4: "0000",
          cardType: "internal",
          totalUsd: totalAmount,
          cryptoSymbol: crypto.symbol,
        },
      });

      if (verifyError) throw verifyError;

      setVerificationResult({
        approved: verifyData.approved,
        analysis: verifyData.analysis,
      });
      setStep("result");
    } catch (error) {
      console.error("Purchase error:", error);
      toast({ title: "فشل في إتمام العملية", description: "حدث خطأ. حاول مرة أخرى.", variant: "destructive" });
      setStep("proof");
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setCryptoInput(""); setErrors({}); setProofFile(null); setProofPreview(null);
    setVerificationResult(null); setStep("details"); setPaymentMethod(null);
    setCardCode(""); setCardBalance(null); setCardError("");
    setWalletAddress(""); setWalletError(""); setProcessingTxId(null); setProcessingCountdown(60);
  };

  const handleClose = () => {
    onClose();
    setTimeout(resetForm, 300);
  };

  // ========== RESULT SCREEN ==========
  if (step === "result" && verificationResult) {
    return (
      <Dialog open={isOpen} onOpenChange={handleClose}>
        <DialogContent className="bg-card border-border max-w-md">
          <div className="flex flex-col items-center justify-center py-8 animate-scale-in">
            {verificationResult.approved ? (
              <>
                <div className="w-20 h-20 rounded-full bg-crypto-green/20 flex items-center justify-center mb-4">
                  <ShieldCheck className="w-12 h-12 text-crypto-green" />
                </div>
                <h3 className="text-2xl font-bold text-foreground mb-2">تمت الموافقة ✅</h3>
                <p className="text-muted-foreground text-center mb-2">
                  تم التحقق والموافقة على المعاملة بنجاح
                </p>
                <p className="text-sm text-primary text-center">
                  تمت إضافة {formatCrypto(cryptoAmount)} {crypto.symbol} إلى محفظتك
                </p>
                {verificationResult.transactionNumber && (
                  <p className="text-xs text-muted-foreground mt-2">
                    رقم المعاملة: <span className="font-mono text-foreground">{verificationResult.transactionNumber}</span>
                  </p>
                )}
                <p className="text-xs text-muted-foreground mt-3 bg-secondary/50 p-2 rounded-lg">
                  {verificationResult.analysis}
                </p>
              </>
            ) : (
              <>
                <div className="w-20 h-20 rounded-full bg-yellow-500/20 flex items-center justify-center mb-4">
                  <Clock className="w-12 h-12 text-yellow-500" />
                </div>
                <h3 className="text-2xl font-bold text-foreground mb-2">قيد المراجعة</h3>
                <p className="text-muted-foreground text-center mb-2">
                  يحتاج إثبات الدفع إلى مراجعة يدوية من الإدارة
                </p>
                <p className="text-xs text-muted-foreground mt-3 bg-secondary/50 p-2 rounded-lg">
                  {verificationResult.analysis}
                </p>
              </>
            )}
            <Button onClick={() => { handleClose(); onSuccess(); }} className="mt-6 gradient-primary text-primary-foreground">
              إغلاق
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  // ========== VERIFYING SCREEN ==========
  if (step === "verifying") {
    const isOnChainPhase = paymentMethod === "yqqy_card" && processingTxId;
    return (
      <Dialog open={isOpen} onOpenChange={() => {}}>
        <DialogContent className="bg-card border-border max-w-md">
          <div className="flex flex-col items-center justify-center py-10">
            <Loader2 className="w-16 h-16 text-primary animate-spin mb-4" />
            <h3 className="text-xl font-bold text-foreground mb-2">
              {isOnChainPhase ? "قيد المعالجة على البلوكتشين..." : "جاري المعالجة..."}
            </h3>
            <p className="text-muted-foreground text-center text-sm px-4">
              {isOnChainPhase
                ? "يقوم المسؤول بتحويل USDC إلى " + crypto.symbol + " وإرسالها مباشرة إلى عنوانك على شبكة Polygon."
                : paymentMethod === "yqqy_card" ? "جاري الخصم من البطاقة..." : "الذكاء الاصطناعي يتحقق من إثبات الدفع"}
            </p>
            {isOnChainPhase && (
              <div className="mt-5 px-4 py-2 rounded-full bg-primary/10 border border-primary/20">
                <span className="text-primary font-bold tabular-nums">
                  ⏳ {processingCountdown}s
                </span>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="bg-card border-border max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-xl font-bold text-foreground flex items-center gap-2">
            {crypto.image ? (
              <img src={crypto.image} alt={crypto.name} className="w-8 h-8 rounded-full" />
            ) : (
              <div className="w-8 h-8 rounded-full flex items-center justify-center text-lg font-bold"
                style={{ background: crypto.color, color: 'hsl(var(--background))' }}>
                {crypto.icon}
              </div>
            )}
            شراء {crypto.nameAr}
          </DialogTitle>
        </DialogHeader>

        {/* Steps indicator */}
        <div className="flex items-center justify-center gap-2 my-3">
          {(["details", "payment_method", paymentMethod === "yqqy_card" ? "card_code" : "proof"] as Step[]).map((s, i) => (
            <div key={s} className="flex items-center gap-2">
              <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold transition-colors ${
                step === s ? "gradient-primary text-primary-foreground" :
                (["details", "payment_method", "card_code", "proof"].indexOf(step) > ["details", "payment_method", "card_code", "proof"].indexOf(s) ? "bg-crypto-green text-white" : "bg-secondary text-muted-foreground")
              }`}>
                {i + 1}
              </div>
              {i < 2 && <div className="w-8 h-0.5 bg-border" />}
            </div>
          ))}
        </div>

        <div className="space-y-5 mt-2 pb-4">
          {/* ========== STEP 1: AMOUNT ========== */}
          {step === "details" && (
            <>
              <div className="flex items-start gap-3 p-3 rounded-lg bg-primary/10 border border-primary/20">
                <ShieldCheck className="w-5 h-5 text-primary mt-0.5 shrink-0" />
                <p className="text-sm text-foreground">
                  العملات ستضاف مباشرة إلى محفظتك الداخلية بعد التحقق من الدفع.
                </p>
              </div>

              <div className="space-y-2">
                <Label className="text-foreground">الكمية ({crypto.symbol})</Label>
                <Input type="number" placeholder={`عدد ${crypto.symbol} الذي تريد شراءه`} value={cryptoInput}
                  min={0} step="any"
                  onChange={(e) => {
                    const val = e.target.value;
                    if (val === '' || parseFloat(val) >= 0) setCryptoInput(val);
                  }}
                  onBlur={() => {
                    const num = parseFloat(amount);
                    if (cryptoInput && (isNaN(num) || num < 5)) setErrors(prev => ({ ...prev, amount: "الحد الأدنى للمبلغ 5 دولار" }));
                    else if (cryptoInput && num > 100000000) setErrors(prev => ({ ...prev, amount: "الحد الأقصى 100,000,000 دولار" }));
                    else setErrors(prev => { const { amount, ...rest } = prev; return rest; });
                  }}
                  className="bg-input border-border text-foreground text-lg" />
                <p className="text-xs text-muted-foreground">
                  سعر العملة: {formatCurrency(crypto.sellPrice)} — الحد الأدنى للمبلغ $5
                </p>
                {errors.amount && <p className="text-sm text-destructive">{errors.amount}</p>}
              </div>

              {cryptoInput && parseFloat(cryptoInput) > 0 && (
                <div className="p-4 rounded-xl bg-primary/10 space-y-2">
                  <div className="flex justify-between text-foreground">
                    <span>الكمية</span>
                    <span>{formatCrypto(cryptoAmount)} {crypto.symbol}</span>
                  </div>
                  <div className="border-t border-border pt-2 mt-2">
                    <div className="flex justify-between text-lg font-bold text-foreground">
                      <span>المبلغ المطلوب دفعه</span>
                      <span className="text-primary">{formatCurrency(parseFloat(amount))}</span>
                    </div>
                  </div>
                  <p className="text-xs text-muted-foreground pt-1">
                    اذهب إلى موقع yqqy.uk واشترِ بطاقة بهذه القيمة ثم أدخل رمزها في الخطوة التالية.
                  </p>
                </div>
              )}

              <Button onClick={handleDetailsNext}
                disabled={!amount || parseFloat(amount) < 5}
                className="w-full h-12 gradient-primary text-primary-foreground font-semibold text-lg">
                التالي — الذهاب إلى الموقع
              </Button>
            </>
          )}

          {/* ========== STEP 2: PAYMENT METHOD ========== */}
          {step === "payment_method" && (
            <>
              <h3 className="text-lg font-bold text-foreground text-center">اختر طريقة الدفع</h3>

              {/* yqqy.uk Card Option */}
              <button
                onClick={() => { setPaymentMethod("yqqy_card"); setStep("card_code"); }}
                className="w-full p-4 rounded-xl border-2 border-primary/30 hover:border-primary bg-primary/5 hover:bg-primary/10 transition-all text-right"
              >
                <div className="flex items-center gap-4">
                  <div className="w-14 h-14 rounded-xl bg-primary/20 flex items-center justify-center shrink-0">
                    <CreditCard className="w-7 h-7 text-primary" />
                  </div>
                  <div className="flex-1">
                    <h4 className="font-bold text-foreground text-base mb-1">💳 بطاقة yqqy (فوري)</h4>
                    <p className="text-xs text-muted-foreground">
                      ادفع برمز بطاقة yqqy.uk — الموافقة فورية وتلقائية
                    </p>
                  </div>
                  <div className="px-2 py-1 rounded-full bg-crypto-green/20 text-crypto-green text-xs font-bold shrink-0">
                    مُوصى
                  </div>
                </div>
              </button>

              <Button variant="outline" onClick={() => setStep("details")} className="w-full h-10">
                رجوع
              </Button>
            </>
          )}

          {/* ========== STEP 3A: CARD CODE ========== */}
          {step === "card_code" && (
            <>
              {/* Instructions */}
              <div className="p-4 rounded-xl bg-primary/5 border border-primary/20 space-y-3">
                <h4 className="font-bold text-foreground flex items-center gap-2">
                  <KeyRound className="w-5 h-5 text-primary" />
                  كيف تحصل على بطاقة yqqy؟
                </h4>
                <ol className="space-y-2 text-sm text-muted-foreground list-decimal list-inside">
                  <li>
                    ادخل إلى موقع{" "}
                    <a href="https://www.yqqy.uk/signup" target="_blank" rel="noopener noreferrer"
                      className="text-primary font-bold hover:underline inline-flex items-center gap-1">
                      yqqy.uk <ExternalLink className="w-3 h-3" />
                    </a>{" "}
                    وسجّل حساب جديد
                  </li>
                  <li>اشترِ بطاقة بقيمة <span className="text-primary font-bold">${amount}</span></li>
                  <li>انسخ <span className="text-primary font-bold">رمز البطاقة</span> والصقه هنا</li>
                </ol>
              </div>

              {/* Card Code Input */}
              <div className="space-y-2">
                <Label className="text-foreground font-bold">رمز البطاقة</Label>
                <div className="flex gap-2">
                  <Input
                    placeholder="أدخل رمز البطاقة هنا..."
                    value={cardCode}
                    onChange={(e) => { setCardCode(e.target.value); setCardError(""); setCardBalance(null); }}
                    className="bg-input border-border text-foreground text-base font-mono flex-1"
                    dir="ltr"
                  />
                  <Button
                    variant="outline"
                    onClick={handleCheckCardBalance}
                    disabled={checkingCard || !cardCode.trim()}
                    className="shrink-0"
                  >
                    {checkingCard ? <Loader2 className="w-4 h-4 animate-spin" /> : "تحقق"}
                  </Button>
                </div>
                {cardError && (
                  <p className="text-sm text-destructive flex items-center gap-1">
                    <AlertTriangle className="w-4 h-4" /> {cardError}
                  </p>
                )}
                {cardBalance !== null && !cardError && (
                  <div className="flex items-center gap-2 text-crypto-green text-sm">
                    <CheckCircle2 className="w-4 h-4" />
                    <span>رصيد البطاقة: <span className="font-bold">${cardBalance}</span></span>
                  </div>
                )}
              </div>

              {/* Auto-delivery notice */}
              <div className="p-3 rounded-lg bg-primary/10 border border-primary/20 text-sm text-foreground flex items-start gap-2">
                <ShieldCheck className="w-5 h-5 text-primary shrink-0 mt-0.5" />
                <p>
                  سيتم إرسال <span className="text-primary font-bold">{crypto.symbol}</span> تلقائياً إلى محفظتك على شبكة Polygon (POS) — لا حاجة لإدخال أي عنوان.
                </p>
              </div>



              {/* Summary */}
              <div className="p-3 rounded-lg bg-secondary/30 text-sm space-y-1">
                <div className="flex justify-between text-foreground">
                  <span>المبلغ المطلوب</span>
                  <span className="font-bold">{formatCurrency(parseFloat(amount))}</span>
                </div>
                <div className="flex justify-between text-foreground">
                  <span>ستضاف إلى محفظتك</span>
                  <span className="text-primary font-bold">{formatCrypto(cryptoAmount)} {crypto.symbol}</span>
                </div>
              </div>

              <div className="flex gap-3">
                <Button variant="outline" onClick={() => setStep("payment_method")} className="flex-1 h-12">
                  رجوع
                </Button>
                <Button
                  onClick={handlePayWithCard}
                  disabled={!cardCode.trim() || loading || (cardBalance !== null && cardBalance < parseFloat(amount))}
                  className="flex-1 h-12 gradient-primary text-primary-foreground font-semibold"
                >
                  ادفع الآن 💳
                </Button>
              </div>
            </>
          )}

          {/* ========== STEP 3B: MANUAL PROOF ========== */}
          {step === "proof" && (
            <>
              <div className="flex items-start gap-3 p-3 rounded-lg bg-primary/10 border border-primary/20">
                <ShieldCheck className="w-5 h-5 text-primary mt-0.5 shrink-0" />
                <p className="text-sm text-foreground">
                  ارفع صورة إيصال الدفع أو لقطة شاشة للمعاملة. سيقوم الذكاء الاصطناعي بالتحقق والموافقة تلقائياً.
                </p>
              </div>

              <div className="p-4 rounded-xl bg-secondary/50 space-y-4">
                <div className="flex items-center gap-2 text-muted-foreground mb-2">
                  <Upload className="w-5 h-5" />
                  <span className="font-medium">إثبات الدفع</span>
                </div>

                <input ref={fileInputRef} type="file" accept="image/*" className="hidden"
                  onChange={handleFileSelect} />

                {proofPreview ? (
                  <div className="relative">
                    <img src={proofPreview} alt="إثبات الدفع" className="w-full rounded-lg max-h-48 object-contain bg-background" />
                    <button onClick={() => { setProofFile(null); setProofPreview(null); }}
                      className="absolute top-2 right-2 w-8 h-8 rounded-full bg-destructive/80 text-destructive-foreground flex items-center justify-center">
                      <XCircle className="w-5 h-5" />
                    </button>
                  </div>
                ) : (
                  <button onClick={() => fileInputRef.current?.click()}
                    className="w-full h-32 rounded-xl border-2 border-dashed border-border hover:border-primary/50 transition-colors flex flex-col items-center justify-center gap-2 text-muted-foreground hover:text-primary">
                    <Upload className="w-8 h-8" />
                    <span className="text-sm">اضغط لرفع صورة الإيصال</span>
                    <span className="text-xs">PNG, JPG حتى 5MB</span>
                  </button>
                )}
              </div>

              <div className="p-3 rounded-lg bg-secondary/30 text-sm space-y-1">
                <div className="flex justify-between text-foreground">
                  <span>المبلغ</span>
                  <span className="font-bold">{formatCurrency(parseFloat(amount))}</span>
                </div>
                <div className="flex justify-between text-foreground">
                  <span>ستضاف إلى محفظتك</span>
                  <span className="text-primary font-bold">{formatCrypto(cryptoAmount)} {crypto.symbol}</span>
                </div>
              </div>

              <div className="flex gap-3">
                <Button variant="outline" onClick={() => setStep("payment_method")} className="flex-1 h-12">
                  رجوع
                </Button>
                <Button onClick={handleSubmitWithProof}
                  disabled={!proofFile || loading}
                  className="flex-1 h-12 gradient-primary text-primary-foreground font-semibold">
                  {loading ? "جاري الإرسال..." : "إرسال للتحقق 🤖"}
                </Button>
              </div>
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default PurchaseModal;
